import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Send, ChevronLeft, IdCard, RefreshCcw, Video, Phone, PhoneOutgoing, Flag, Trash2 } from 'lucide-react';
import { useSessionContext } from '@supabase/auth-helpers-react';
import { User } from '@supabase/supabase-js';
import storage from 'local-storage-fallback';

import { supabase, SUPABASE_URL, supabaseEh } from '../lib/supabase';
import type { Database, Json } from '../types/database';
import { formatMessageText } from '../components/formatMessageText';
import { CharacterSummaryDialog, CharacterSummaryContent } from '../components/CharacterSummaryDialog';
import EhAuthDialog from '../components/EhAuthDialog';
import { EhUpgradeDialog } from '../components/EhUpgradeDialog';
import { getMediaUrl, omit, getDisplayName, formatMessageTime, isCustomCharacter, isVideoUrl } from '../lib/utils';
import { generateBase58Id, getIdWithoutPrefix, TABLE_PREFIXES } from '../lib/id';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import ChatListView, { ChatListItem } from '../components/ChatListView';
import EhImageMessage from '../components/EhImageMessage';
import EhVideoMessage from '../components/EhVideoMessage';
import { SEO } from '../components/SEO';
import { useUserInfo } from '../hooks/useUserInfo';
import { useTracking, UI_EVENTS, USER_EVENTS } from '../hooks/useTracking';
import { useChatTracking } from '../hooks/useChatTracking';
import { useMessageDraft } from '../hooks/useMessageDraft';
import { useTTS } from '../hooks/useTTS';
import { useScreenBreakpoints } from '@/hooks/useScreenBreakpoints';
import { useEhLayout } from '@/components/EhLayout';
import { TTSButton } from '../components/TTSButton';
import { EhGemsDialog } from '@/components/EhGemsDialog';
import type { MessageMetadata } from '../lib/types';
import { Textarea } from '@/components/ui/textarea';
import { EH_HEADER_HEIGHT } from '@/components/EhHeader';
import { useGemBalance } from '@/hooks/useGemBalance';
import { Button } from '@/components/ui/button';
import { useCall } from '@/contexts/CallContext';
import { EhEndorselyScript } from '@/components/EhEndorselyScript';
import { isFromAppStores } from '@/lib/detection';
import { EditCharacterDialog } from '@/components/EditCharacterDialog';
import { EhFlagDialog } from '@/components/EhFlagDialog';
import {
  findBestVoiceForLanguage,
  COST_PER_VIDEO,
  LS_TTS_VOICE_SELECTION_SHOWN_KEY,
  MAX_UI_MESSAGES,
  SUPPORTED_TTS_LANGUAGES,
  VOICE_OPTIONS,
} from '../lib/constants';

const AVATAR_SIZE = 78;
const MAX_MESSAGE_LENGTH = 2000;

const FREE_VIDEOS = 1;

type Character = Database['eh']['Tables']['characters']['Row'];
type Message = Database['eh']['Tables']['messages']['Row'];
type CharacterSettings = Partial<Database['eh']['Tables']['settings']['Row']> | null;
type CharacterChats = Partial<Database['eh']['Tables']['chats']['Row']>[] | [];

type MessageMediaData = {
  call_duration?: number; // call duration in seconds
  img_description?: string;
  img_prompt?: string;
  video_img_message_id?: string;
  video_prompt?: string;
  mr_id?: string;
  requestId?: string;
  character_sender_id?: string;
  blur?: boolean; // whether the image should be blurred for non-subscribers after free trial limit
  is_charged?: boolean; // whether the image was charged gems for
  is_explicit?: boolean; // whether a vision model detected explicit content
  nsfw?: boolean; // whether nsfw mode was enabled for this request
  safe_mode?: boolean; // whether the request was in safe mode (age not confirmed)
  ir_failed?: boolean; // image request failed, set on img_request_message_id message)
  ir_low_balance?: boolean; // low balance failing image request, set on img_request_message_id message
  face_error?: boolean; // whether the face could not be detected in input image
  skip_input_img_after_face_error?: boolean; // generated without character face as input
  [key: string]: Json | undefined;
};

export default function ChatPage() {
  const navigate = useNavigate();
  const { charSlug, chatIdSlug } = useParams<{ charSlug?: string; chatIdSlug?: string }>();
  const [searchParams] = useSearchParams();
  const { session, isLoading: isSessionLoading } = useSessionContext();
  const { isSubscriber, isUnlimited, is18Confirmed, userDetails, updateUserDetails } = useUserInfo();
  const { trackEvent } = useTracking();
  const { saveDraft, loadDraft, deleteDraft, debouncedSaveDraft } = useMessageDraft();
  const { 
    trackChatCreated, 
    trackChatError,
    trackMessageReceived,
    trackMessageSent,
    trackImageRequested,
    trackCharacterFromImageGenerated,
    trackChatDeleted
  } = useChatTracking();
  const { stopAllAudio } = useTTS();
  const { isLargeScreen } = useScreenBreakpoints();
  const { headerRef } = useEhLayout();
  const { balance, refreshBalance } = useGemBalance();
  const { preCall, callState } = useCall();

  const chatId = useRef<string>(chatIdSlug ? `${TABLE_PREFIXES.chats}_${chatIdSlug}` : '');
  const [character, setCharacter] = useState<(
    Character & { chats: CharacterChats, settings: CharacterSettings }
  ) | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingCharacter, setLoadingCharacter] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [isEditCharacterDialogOpen, setIsEditCharacterDialogOpen] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [videoStatus, setVideoStatus] = useState<'idle'|'pending'|'success'|'failed'>('idle');
  const [pendingVideoOriginMsgId, setPendingVideoOriginMsgId] = useState('');
  const [pendingVideoDetectionDone, setPendingVideoDetectionDone] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messageFormRef = useRef<HTMLFormElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const [chatList, setChatList] = useState<ChatListItem[]>([]);
  const [chatListLoading, setChatListLoading] = useState(false);
  const [chatListError, setChatListError] = useState<string | null>(null);
  const [showChatList, setShowChatList] = useState(false);
  const [customImage, setCustomImage] = useState<string>('');
  const isGeneratingCharacter = useRef(false);
  const authDialogRef = useRef<{ checkOnboarding: () => Promise<boolean> }>({
    checkOnboarding: async () => {
      if (import.meta.env.DEV) console.log('[DEV] Missing ref to EhAuthDialog.checkOnboarding')
      return false;
    }
  });
  const messageSubscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
  const waitingResponseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const videoRetryCountRef = useRef(0);
  const videoRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const videoAbortControllerRef = useRef<AbortController | null>(null);
  const videoAbortTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref to track last chat ID for draft restoration
  const lastChatIdForDraft = useRef<string>('');

  // Add state for upgrade dialog
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [limitData, setLimitData] = useState<{
    limitType: 'audio' | 'call' | 'text' | 'image' | 'nsfw' | 'video' | 'create';
    // usageStats?: {
    //   textMessages: number;
    //   images: number;
    //   textLimit: number;
    //   imageLimit: number;
    // };
  }>({ limitType: 'text' });
  const showUpgradeDialogTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Add state for upgrade dialog for NSFW toggle
  const teaseNsfwTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showNsfwUpgradeDialog, setShowNsfwUpgradeDialog] = useState(false);
  const [teaseNsfwEnabled, setTeaseNsfwEnabled] = useState(false);
  
  const [lowGemBalanceAction, setLowGemBalanceAction] = useState<'tts' | 'image' | 'video' | ''>('');
  const [showGemsDialog, setShowGemsDialog] = useState(false);
  const [showFlagDialog, setShowFlagDialog] = useState(false);
  const [flaggedMessage, setFlaggedMessage] = useState<Message | null>(null);
  
  // TTS language detection state
  const [ttsLanguageContext, setTtsLanguageContext] = useState<string>('');
  const [ttsSelectedVoice, setTtsSelectedVoice] = useState<string>('');
  
  const isCallActive = callState === 'connected' || callState === 'connecting';

  const handleStartCall = async () => {
    if (!session?.user) {
      setAuthDialogOpen(true);
      return;
    }
    if (!chatId.current) await loadChatResources();
    if (!character || !chatId.current) return; // failsafe
    if (!isSubscriber) {
      setLimitData({ limitType: 'call' });
      setShowUpgradeDialog(true);
      return;
    }
    preCall(character, chatId.current);
  };

  const handleEditCharacterSuccess = (newCharacter: Character) => {
    setCharacter(prev => prev ? { ...prev, ...newCharacter } : null);
    setTtsLanguageContext('');
  };

  const isChatWithMeRoute = charSlug === 'chat-with-me';
  // Handle chat-with-me route
  useEffect(() => {
    if (isChatWithMeRoute) {
      const genderParam = searchParams.get('g');
      const imageParam = searchParams.get('i');
      const promptParam = searchParams.get('p');
      const styleParam = searchParams.get('s');

      // avoid simultaneous runs
      if (isGeneratingCharacter.current) return;
      isGeneratingCharacter.current = true;
      
      // Validate image URL domain is allowed
      const isAllowedImage = (() => {
        if (!imageParam) return false;
        try {
          const url = new URL(imageParam);
          // Allow only from VITE_AIHP_SUPABASE_URL for now
          const allowedDomains = [import.meta.env.VITE_AIHP_SUPABASE_URL];
          return allowedDomains.some(domain => url.origin.includes(domain));
        } catch {
          return false;
        }
      })();
      
      if (!imageParam ||!isAllowedImage) {
        // Invalid or missing image URL - redirect to home page
        if (import.meta.env.DEV) console.error('[DEV] Invalid image URL:', imageParam);
        navigate('/', { replace: true });
        return;
      }
      
      // Valid image URL - store for display while character is being generated
      setCustomImage(imageParam);
      // setCustomPrompt(promptParam || '');
      setLoadingCharacter(true);
      
      // Call the edge function to generate or retrieve character
      const generateCharacter = async () => {
        try {
          const { data, error } = await supabase.functions.invoke('eh-new-char', {
            body: { 
              gender: genderParam,
              imageUrl: imageParam,
              prompt: promptParam,
              style: styleParam,
            }
          });
          
          if (error) throw new Error(error.message);
          if (!data?.character) throw new Error('No character data returned');
          
          setCharacter(data.character);
          
          // Track character generation
          trackCharacterFromImageGenerated({
            characterId: data.character.id,
            imageUrl: imageParam,
            success: true
          });
          
          if (import.meta.env.DEV) {
            if (data.reused) {
              console.log('[DEV] Reusing existing character:', data.character.url_slug);
            } else {
              console.log('[DEV] Generated new character:', data.character.url_slug);
            }
          }
          
          // Update URL to point to the real character
          navigate(`/c/${data.character.url_slug}`, { replace: true });
        } catch (err) {
          setError('Failed to generate character');
          
          // Track character generation error
          trackCharacterFromImageGenerated({
            characterId: 'unknown',
            imageUrl: imageParam,
            success: false,
            error: `Failed to generate character: ${err instanceof Error ? err.message : 'Unknown error'}`
          });
          
          if (import.meta.env.DEV) {
            console.error('[DEV] Character generation error:', err);
          }
        } finally {
          setLoadingCharacter(false);
          setLoading(false);
          isGeneratingCharacter.current = false;
        }
      };
      
      generateCharacter();
    }
  }, [isChatWithMeRoute, navigate, searchParams, trackCharacterFromImageGenerated]);

  // DRAFT effects
  // Restore draft when chat changes (or when exploring characters with no existing chat)
  useEffect(() => {
    if ((chatId.current && chatId.current !== lastChatIdForDraft.current) || charSlug) {
      const draft = loadDraft((chatId.current || charSlug) as string);
      if (draft) setInput(draft);
      else setInput('');
      if (chatId.current) lastChatIdForDraft.current = chatId.current;
      
      // Reset TTS voice selection dialog state when chat changes
      setTtsLanguageContext('');
      setTtsSelectedVoice('');
    }
  }, [charSlug, chatIdSlug, loadDraft]); // Depend on URL param instead of ref

  // Also restore draft after messages are fetched (indicates chatId is ready)
  useEffect(() => {
    if (chatId.current && chatId.current !== lastChatIdForDraft.current && messages.length >= 0) {
      const draft = loadDraft(chatId.current);
      if (draft) {
        setInput(draft);
      } else if (lastChatIdForDraft.current) { // Only clear if we had a previous chat
        setInput('');
      }
      lastChatIdForDraft.current = chatId.current;
    }
  }, [messages.length, loadDraft]); // Trigger when messages are loaded
  // END: DRAFT effects

  // Scroll to bottom on new messages or when waiting for response
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, isWaitingForResponse]);

  const scrollToBottomOnLoad = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      node.scrollIntoView({ behavior: 'instant' });
    }
  }, []);

  // Fetch character (after charSlug is set or user logs in)
  useEffect(() => {
    if (!charSlug || isChatWithMeRoute || loadingCharacter) return;
    setLoading(true);
    const charQuery = supabaseEh
      .from('characters')
      .select('*, chats!chats_character_id_fkey(id, created_at, deleted_at), settings:settings_id(messaging_mode, nsfw)')
      .is('chats.deleted_at', null)
      .order('created_at', { referencedTable: 'chats', ascending: false })
      .limit(3, { referencedTable: 'chats' })
      .eq('url_slug', charSlug);
    
    const runCharQuery = (q: typeof charQuery) => {
      q.then(({ data, error }) => {
          if (error || !data || data.length === 0) {
            if (import.meta.env.DEV) console.error('[DEV] Character not found:', error);
            setError('Character not found');
            setCharacter(null);
          } else {
            const sortedChars = data.sort((a, b) => { // chars with most recent chat first
              if (!a.chats.length && !b.chats.length) return -a.created_at.localeCompare(b.created_at);
              if (!a.chats.length) return 1;
              if (!b.chats.length) return -1;
              return -a.chats[0].created_at.localeCompare(b.chats[0].created_at);
            });
            setCharacter(sortedChars[0]);
            // MARK: Automatically reuse existing chat if available
            // and no chatId is set (in case of multiple existing conversations with same character)
            // Creating parallel chats with same character will be a premium feature.
            if (sortedChars[0].chats.length && !chatId.current) {
              chatId.current = sortedChars[0].chats[0].id;
              navigate(`/c/${charSlug}/${chatId.current.replace('chat_', '')}`);
            }
            setError(null);
          }
          setLoading(false);
        });
    }

    if (chatId.current) {
      supabaseEh
        .from('chats')
        .select(`*`)
        .eq('id', chatId.current)
        .is('deleted_at', null)
        .maybeSingle()
        .then(({ data, error }) => {
          if (error || !data) {
            if (import.meta.env.DEV) console.error('[DEV] Chat not found:', error);
            setError('Chat not found');
            setCharacter(null);
            setLoading(false);
          } else {
            runCharQuery(charQuery.eq('id', data.character_id || ''));
          }
        });
    } else { // If no chatId, fetch all characters, and pick the one with (the most recently created) chat: see above
      runCharQuery(charQuery.in('character_scope', ['private', 'public', 'random']));
    }
  }, [charSlug, isChatWithMeRoute, loadingCharacter, navigate]);

  /**
   * Get the status of the image request for a message
   * @param messages - All chat messages to check against (required paramater to avoid circular dependencies)
   * @param message - The message to check (optional: last message by default)
   * @returns The status of the image request
   */
  const getImageRequestStatus = useCallback((messages: Message[], message?: Message) => {
    const msg = message || messages[messages.length - 1];
    const requestISODate = (msg.msg_metadata as MessageMetadata)?.image_requested_at
    if (!requestISODate) return 'none';

    const requestDateObj = new Date(requestISODate);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - requestDateObj.getTime());
    const diffMinutes = Math.round(diffTime / (1000 * 60));
    const isRecent = diffMinutes < 60;
    const isFulfilled = messages.some(m => 
      m.msg_type === 'image' && // image request fulfilled for this message
      (m.msg_media_data as MessageMediaData)?.img_request_message_id === msg.id
    )
    const hasFaceError = (msg.msg_media_data as MessageMediaData)?.face_error;
    const hasError = (msg.msg_media_data as MessageMediaData)?.ir_failed;
    const hasLowBalance = (msg.msg_media_data as MessageMediaData)?.ir_low_balance;

    // order matters
    if (hasLowBalance) return 'low_balance';
    if (hasFaceError) return 'face_error';
    if (hasError) return 'failed'; // show a soft error in the UI, just in case
    if (isFulfilled) return 'fulfilled';
    if (isRecent) return 'pending';
    return 'expired';
  }, []);

  const hasGeneratedVideoForThisImage = useCallback((messages: Message[], message?: Message) => {
    const msg = message || messages[messages.length - 1];
    return messages.some(m => 
      m.msg_type === 'video' && // video request fulfilled for this message
      (m.msg_media_data as MessageMediaData)?.video_img_message_id === msg.id
    )
  }, []);

  /**
   * Fetch messages from the database
   * @param nbPreviousMessages - The current number of messages to ignore for tracking
   * @returns The messages and an error if there is one
   */
  const fetchMessages = useCallback(async ({ nbPreviousMessages = 0 }: {
    nbPreviousMessages?: number
  } = {}) => {
    const user = (await supabase.auth.getSession()).data.session?.user;
    if (!chatId.current || !user) return { data: [], error: 'Missing chatId or user' };
    return supabaseEh
      .from('messages')
      .select('*')
      .eq('chat_id', chatId.current)
      .order('created_at', { ascending: false }) // last messages first due to limit
      .limit(MAX_UI_MESSAGES)
      .then(({ data, error }) => {
        // if (import.meta.env.DEV) console.log('[DEV] Setting messages:', data);
        setMessages(prev => {
          try {
            if (data && data.length > 0 && nbPreviousMessages) {
              if (import.meta.env.DEV) console.log(`[DEV] Previously ${prev.length} messages (control ${nbPreviousMessages})`);
              data.forEach((msg, index) => { // track messages received from AI
                const nbFreshMessages = data.length - nbPreviousMessages;
                if (index < nbFreshMessages && msg.sender_id === null) {
                  trackMessageReceived({
                    chatId: chatId.current,
                    characterId: character?.id,
                    message: msg,
                  });
                }
              });
            }
          } catch (err) {
            if (import.meta.env.DEV) console.error('[DEV] Error tracking messages:', err);
          }
          return (data || []).slice().reverse();
        });
        // MARK: Fallback when messages are fetched here while waiting for response (e.g. session refresh)
        // but could break some edge cases or future realtime features in the future
        const d = data || [];
        const lastMessage = d[0];
        const tenMinutesAgo = new Date().getTime() - 10 * 60 * 1000;
        if (lastMessage) { // set writing bubble or loading animation only if pending image or unanswered recent user message
          setIsWaitingForResponse(
            d.some(msg => getImageRequestStatus(d, msg) === 'pending')
              || Boolean(lastMessage.sender_id && new Date(lastMessage.created_at).getTime() > tenMinutesAgo)
          );
        } else {
          setIsWaitingForResponse(false);
        }
        // END:MARK
        // Update chat list with last message without to avoid re-fetching all chats
        setChatList(prev => {
          const chat = prev.find(c => c.id === chatId.current);
          if (chat && lastMessage) chat.lastMessage = lastMessage;
          return prev;
        });

        return { data, error };
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [character?.id, getImageRequestStatus]); // omit trackMessageReceived to avoid re-rendering on navigation

  // Fetch messages if chatId is present and authenticated
  // Or redirect to Home page if not authenticated anymore or no charSlug (chat preview)
  useEffect(() => {
    if (import.meta.env.DEV) console.log('[DEV] ChatPage fetchMessages useEffect', session?.user, isSessionLoading, chatId.current);
    if (!session?.user && !isSessionLoading && chatId.current) {
      navigate('/');
      return;
    } else if (!session?.user && !isSessionLoading && !charSlug) {
      navigate('/', { replace: true });
      return;
    }
    // In some edge cases, such at new pending chat, unauthenticated users can stay on ChatPage.
    // We need to reset the state to avoid any confusion.
    if (!session?.user) {
      setMessages([]);
      setChatList([]);
    }
    if (!chatId.current) return;

    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'instant' });
    }

    void fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charSlug, fetchMessages, isSessionLoading, session?.user]); // omit navigate to avoid re-rendering on navigation

  // Refresh pending video status
  useEffect(() => {
    if (!messages.length || !chatList.length || !chatId.current) return;

    const chat = chatList.find(c => c.id === chatId.current);
    const pendingVideoOriginMsgId = chat?.chat_metadata?.pending_video_origin_msg_id;

    if (chat && !pendingVideoOriginMsgId) setPendingVideoDetectionDone(true);
    if (!chat || !pendingVideoOriginMsgId) return;

    // Pending video status
    const oneHourAgo = new Date().getTime() - 1000 * 60 * 60;
    const isVideoRecentlyPending = chat?.chat_metadata?.pending_video_at && new Date(chat.chat_metadata.pending_video_at).getTime() > oneHourAgo;
    const pendingVideoMrId = chat?.chat_metadata?.pending_video_mr_id;
    const mrCompleted = pendingVideoMrId && messages.some(msg => (msg.msg_media_data as MessageMediaData)?.mr_id === pendingVideoMrId);
    // Not reliable to simply detect existing videos based on same message
    // since we can have multiple videos generated from the same video_img_message_id.
    // We rather reset the pending video status server-side
    // const isVideoReady = data?.some(msg => (msg.msg_media_data as MessageMediaData)?.video_img_message_id === pendingVideoOriginMsgId);
    if (pendingVideoOriginMsgId && isVideoRecentlyPending && !mrCompleted) {
      setPendingVideoOriginMsgId(pendingVideoOriginMsgId);
      const sourceMessage = messages?.find(msg => msg.id === pendingVideoOriginMsgId) ?? { id: pendingVideoOriginMsgId };
      // Get latest media request status
      if (import.meta.env.DEV) console.log('[DEV] Refreshing pending video status:', pendingVideoOriginMsgId, pendingVideoMrId);
      handleRequestVideo(sourceMessage as Message, { mrId: pendingVideoMrId });
    }
    setPendingVideoDetectionDone(true);
    // omit handleRequestVideo for which we don't want to re-render
    // messages.length is enough as well, to avoid re-rendering on every fetchMessages
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatList, messages.length]);

  const notWaitingForResponseAnymore = useCallback(() => {
    if (waitingResponseTimeoutRef.current) {
      clearTimeout(waitingResponseTimeoutRef.current);
      waitingResponseTimeoutRef.current = null;
    }
    if (teaseNsfwTimeoutRef.current) {
      clearTimeout(teaseNsfwTimeoutRef.current);
      teaseNsfwTimeoutRef.current = null;
    }
    if (showUpgradeDialogTimeoutRef.current) {
      clearTimeout(showUpgradeDialogTimeoutRef.current);
      showUpgradeDialogTimeoutRef.current = null;
    }
    setIsWaitingForResponse(false);
  }, []);

  // Subscribe to new messages (can also be useful if another human is in the chat once multi-user is implemented)
  // Real-time is also useful to get any generated image in real-time when available
  const subscribeToMessages = useCallback(() => { 
    if (!chatId.current || !session?.user) return;

    if (messageSubscriptionRef.current) {
      messageSubscriptionRef.current.unsubscribe();
      messageSubscriptionRef.current = null;
    }

    // We need to use the base supabase client for realtime, not the schema client
    const channel = supabase
      .channel('eh-chat-messages')
      .on(
        'postgres_changes',
        { 
          event: 'INSERT',
          schema: 'eh',
          table: 'messages',
          filter: `chat_id=eq.${chatId.current}`
        },
        (payload) => {
          const newMessage = payload.new as Message;
          const lastMessage = messages[messages.length - 1];
          const isCurrentChat = (msgs: { chat_id: string | null }[]) => msgs.some(msg => msg.chat_id === newMessage.chat_id);
          const replaceOptimistic = lastMessage?.id === 'sending' && newMessage.sender_id === session?.user?.id;

          // Only update messages if we don't already have this message (including any optimistic update to replace)
          if (replaceOptimistic) {
            if (import.meta.env.DEV) console.log('[DEV] Replacing optimistic message:', newMessage, messages);
            setMessages(prev => isCurrentChat(prev) ? [...prev.slice(0, -1), newMessage] : prev);
          } else {
            // check for any fresh message in setter to avoid duplicates
            setMessages(prev => prev.some(msg => msg.id === newMessage.id) || !isCurrentChat(prev)
              ? prev
              : [...prev, newMessage]
            );

            if (import.meta.env.DEV) console.log('[DEV] New message:', newMessage, messages);
            // If this is an AI response, stop the loading state
            if (newMessage.sender_id === null) {
              notWaitingForResponseAnymore();

              trackMessageReceived({
                chatId: chatId.current,
                characterId: character?.id,
                message: newMessage,
              });

              if (newMessage.msg_type === 'image') { // unsubscribe on image request fulfillment, not text answer
                unsubscribe();
              }
            }
          }
        }
      )
      .on(
        'postgres_changes',
        { 
          event: 'UPDATE',
          schema: 'eh',
          table: 'messages',
          filter: `chat_id=eq.${chatId.current}` // AND msg_media_data->>'ir_failed' = 'true'
        },
        (payload) => {
          const newMessage = payload.new as Message;
          if (import.meta.env.DEV) console.log('[DEV] Message update:', payload);
          if (!newMessage.msg_media_data) return; // only kind of update expected for now
          setMessages(prev => {
            const index = prev.findIndex(msg => msg.id === newMessage.id);
            if (index !== -1) {
              prev[index] = newMessage;
              if ((newMessage.msg_media_data as MessageMediaData)?.ir_failed) {
                unsubscribe();
              }
            }
            return prev;
          });
        }
      )
      .subscribe();
    
    const pollingInterval = setInterval(() => { // Polling as fallback to realtime
      fetchMessages({ nbPreviousMessages: messages.length });
    }, 10000);
    
    const unsubscribe = () => {
      supabase.removeChannel(channel);
      messageSubscriptionRef.current = null;
      clearInterval(pollingInterval);
    };
    messageSubscriptionRef.current = { unsubscribe };
  }, [fetchMessages, session?.user, messages, notWaitingForResponseAnymore, character?.id, trackMessageReceived]);

  // Cleanup subscription on unmount
  useEffect(() => {
    return () => {
      messageSubscriptionRef.current?.unsubscribe();
      if (waitingResponseTimeoutRef.current) {
        clearTimeout(waitingResponseTimeoutRef.current);
        waitingResponseTimeoutRef.current = null;
      }
      if (videoRetryTimeoutRef.current) {
        clearTimeout(videoRetryTimeoutRef.current);
      }
      if (videoAbortTimeoutRef.current) {
        clearTimeout(videoAbortTimeoutRef.current)
      }
      if (videoAbortControllerRef.current) {
        videoAbortControllerRef.current.abort('ChatPage unmount');
        videoAbortControllerRef.current = null;
      }

      // Stop any playing audio
      stopAllAudio();
      // Save any unsaved draft before unmounting
      // Fallback to charSlug if chatId is not set, for new chats to be created
      if ((chatId.current || charSlug) && input.trim()) {
        saveDraft((chatId.current || charSlug) as string, input); // Use immediate save on unmount
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // omit input

  // Show starter message if anonymous or if chat has no starter message
  // TODO: user instant messaging starter message if choice made in settings
  const starterMessage = character?.chat_starter_rp || character?.chat_starter_im || '*Say hi!*';
  const starterMessageObject = {
    id: 'starter',
    chat_id: chatId.current,
    created_at: new Date().toISOString(),
    msg_text: starterMessage,
    msg_type: 'text',
    sender_id: null, // character?.id, // null => AI message
  } as Message;
  
  // Helper to get profile image or fallback
  const getProfileImageUrl = (character: Character | null) => {
    if (customImage && isChatWithMeRoute) return customImage;
    if (!character) return '';
    if (character.profile_media_fpaths && character.profile_media_fpaths.length > 0) {
      return getMediaUrl(character.profile_media_fpaths[0]);
    }
    if (character.avatar_fpath) return getMediaUrl(character.avatar_fpath);
    return '';
  };

  let displayMessages = chatId.current && session?.user ? 
    // Check if there are messages and if the first message is from the character
    (messages.length > 0 ? 
      // If the first message is not from a character (aka it's from a user), prepend the starter message
      (messages[0]?.sender_id ? 
        [
          { ...starterMessageObject, created_at: messages[0]?.created_at || new Date().toISOString() },
          ...messages
        ] 
        : messages)
      : [starterMessageObject]
    )
    : [starterMessageObject];

  /** Synthetic profile image or video message */
  const getProfileImageOrVideoMsg = (character: Character | null, profileMediaUrl?: string) => {
    const mediaUrl = profileMediaUrl || getProfileImageUrl(character);
    
    return {
      id: isVideoUrl(mediaUrl) ? 'profile-video' : 'profile-img',
      chat_id: chatId.current,
      created_at: character?.created_at || new Date().toISOString(),
      msg_text: '',
      msg_type: isVideoUrl(mediaUrl) ? 'video' : 'image',
      msg_media_fpath: mediaUrl, // using full URL instead of a path must be supported by UI and backend
      sender_id: null, // null => AI message
    } as Message;
  }
  
  // Inject synthetic profile image or video message for mobile if enabled
  const profileImgUrl = getProfileImageUrl(character);
  if (
    !isLargeScreen &&
    profileImgUrl &&
    displayMessages.length < MAX_UI_MESSAGES &&
    !displayMessages.some(m => m.id === 'profile-img' || m.id === 'profile-video')
  ) {
    const videoProfileUrl = getMediaUrl(character?.profile_media_fpaths.find(isVideoUrl));
    displayMessages = [
      getProfileImageOrVideoMsg(character, videoProfileUrl || profileImgUrl),
      ...displayMessages,
    ];
  }

  const blockExplicitImage = (msg: Message) => {
    const meta = (msg.msg_media_data ?? {}) as MessageMediaData;
    if (!meta.is_explicit) return false;
    return (isFromAppStores() && !meta.nsfw) // block if image is explicit while nsfw was not enabled
      // || isFromAppleAppstore()
      || meta.safe_mode;
  };

  // Hide empty messages unless it's the last one pending an image request
  // Hide flagged and unsafe images
  displayMessages = displayMessages.filter((msg, idx) => {
    const isLastMessage = idx === displayMessages.length - 1;
    if ((msg.msg_type === 'text' && !msg.msg_text && !isLastMessage)
      || msg.flagged
      || (msg.msg_type === 'image' && blockExplicitImage(msg))) {
      return false;
    }
    return true;
  });
  
  const getMessageImage = (messageId?: string, { fullUrl = false } = {}) => {
    if (!messageId) return;
    const msg = messages.find(m => m.id === messageId && m.msg_type === 'image');
    return (fullUrl ? getMediaUrl(msg?.msg_media_fpath ?? '') : msg?.msg_media_fpath) || undefined;
  }

  // Pending video placeholder if a video is being generated in current chat
  if (videoStatus === 'pending' && displayMessages.some(m => m.id === pendingVideoOriginMsgId)) {
    const originMsg = displayMessages.find(m => m.id === pendingVideoOriginMsgId)
    displayMessages = [
      ...displayMessages, {
        id: 'pending-video',
        chat_id: originMsg?.chat_id,
        created_at: character?.created_at || new Date().toISOString(),
        msg_text: '',
        msg_type: 'image',
        msg_media_fpath: getMessageImage(pendingVideoOriginMsgId) || profileImgUrl,
        sender_id: character?.id,
      } as Message
    ]
  }

  // Unified function to send a message to the character using Supabase Edge Function
  const sendMessageToCharacter = useCallback(async (msg: string, { prependStarter }: { prependStarter?: boolean } = {}) => {
    // keep references in case of navigation just after sending
    const chat_id = chatId.current;
    const slug = charSlug;

    const user = (await supabase.auth.getSession()).data.session?.user;
    if (!user || !chat_id || !msg.trim()) return;

    let messagesCount = 0;
    setMessages(prev => {
      if (prev.length > 0 && prev[0].chat_id !== chat_id) {
        return prev; // do nothing if we're not in the right chat after navigation
      }
      messagesCount = prev.length + 1; // for tracking purposes
      return [
        ...prev,
        {
          id: 'sending',
          chat_id,
          sender_id: user.id,
          msg_text: msg,
          msg_type: 'text',
          created_at: new Date().toISOString(),
        } as Message
      ];
    });
    setInput('');
    
    // Delete draft since we're sending the message
    deleteDraft(chat_id);
    if (slug) deleteDraft(slug); // in case we just created a new chat

    try {
      waitingResponseTimeoutRef.current = setTimeout(() => { // simulate delay
        setIsWaitingForResponse(true);
      }, 1000);
      
      // Track message sent
      trackMessageSent({
        chatId: chat_id,
        messageLength: msg.length,
        messageType: 'text',
        characterId: character?.id || 'unknown'
      });
      
      const { data, error } = await supabase.functions.invoke('eh-chat-message', {
        body: {
          chatId: chat_id,
          message: msg,
          messageType: 'text',
          ...(prependStarter ? { starterMessage } : {}),
          // TODO: don't inject this if settings.timeAwareness is false
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          localTime: new Date().toLocaleString(undefined, { dateStyle: 'full', timeStyle: 'full' }),
        },
      });
      if (error) throw error;
      // The real message will be added by the realtime subscription
      // Check if user has reached usage limits
      if (data.limitReached) {
        if (import.meta.env.DEV) console.log('Usage limit reached:', data.limitType);
        setLimitData({
          limitType: data.lt,
          // usageStats: data.usageStats
        });
        setShowUpgradeDialog(true);
        setIsSending(false);
        notWaitingForResponseAnymore();
        setMessages(prev => prev.filter(m => m.id !== 'sending')); // remove optimistic message
        setInput(msg); // restore input
        
        // Save the restored input as draft
        saveDraft(chatId.current, msg);
        return;
      }
      
      // Message sent successfully, fetch messages
      void fetchMessages({ nbPreviousMessages: messagesCount });
      
      // If there's an image request, open supabase realtime websocket
      if (data.hasImageRequest) {
        // Track image request
        trackImageRequested({
          chatId: chat_id,
          characterId: character?.id || 'unknown',
          messageId: data.messageId,
          ...(data.lowGemBalance && { lowGemBalance: data.lowGemBalance }),
        });

        if (!(data.lowGemBalance && isSubscriber)) {
          if (import.meta.env.DEV) console.log('[DEV] Opening realtime websocket for image request:', data.messageId); 
          subscribeToMessages();
        } else {
          await refreshBalance();
          setLowGemBalanceAction('image');
          setShowGemsDialog(true);
          notWaitingForResponseAnymore();
        }
      } else {
        notWaitingForResponseAnymore(); 
      }
    } catch (err) {
      notWaitingForResponseAnymore(); // AI probably won't respond if this fails
      
      // Track chat error
      trackChatError({
        chatId: chat_id,
        characterId: character?.id || 'unknown',
        error: `Failed to send message: ${err instanceof Error ? err.message : 'Unknown error'}`
      });
      
      if (import.meta.env.DEV) {
        setError(`[DEV] ${err instanceof Error ? err.message : 'Failed to send message'}`);
        console.error('[DEV] Failed to send message:', err);
      } else {
        setError('Failed to send message');
      }
      setInput(msg); // restore input
      
      // Save the restored input as draft
      saveDraft(chat_id, msg); // Use immediate save for error recovery
    } finally {
      setIsSending(false);
    }
  }, [
    charSlug, fetchMessages, notWaitingForResponseAnymore, subscribeToMessages,
    trackMessageSent, trackImageRequested, trackChatError, character?.id, starterMessage,
    deleteDraft, saveDraft,
    isSubscriber, refreshBalance,
  ]);

  // Handle send message
  const handleSend = async () => {
    if (isSending) return;
    setIsSending(true);
    if (!session?.user && !isSessionLoading) {
      setPendingMessage(input);
      setAuthDialogOpen(true);
      setIsSending(false);
      return;
    }

    const onboardingCompleted = await authDialogRef.current?.checkOnboarding();
    if (!onboardingCompleted) {
      setIsSending(false);
      return;
    }

    // User is logged in, send the message
    if (chatId.current) {
      await sendMessageToCharacter(input, { prependStarter: messages.length === 0 });
    } else { // create chat and send first message
      await loadChatResources(input);
    }
    // setInput(''); // depends on request status, handled by sendMessageToCharacter
    setIsSending(false);
  };

  async function loadChatResources(firstMessageToSend?: string, { askingVideo = false } = {}) {
    const user = (await supabase.auth.getSession()).data.session?.user;
    if (!user || !character) return;
    setLoading(true);
    let now = new Date().toISOString();
    let privateChar: Character | undefined;

    try {
      // 1. Check for existing private character copy, unless it's a custom character we can use directly
      let privateCharId: string | null = null;
      if (isCustomCharacter(character)) {
        privateChar = character;
        privateCharId = character.id;
      }

      if (!privateCharId) {
        const { data: existingChars } = await supabaseEh
          .from('characters')
          .select('*')
          .eq('parent_char_id', character.id)
          .eq('created_by', user.id)
          .eq('character_scope', 'private');
        if (existingChars && existingChars.length > 0) {
          privateChar = existingChars[0];
          privateCharId = privateChar.id;
        }
      }

      // 2. If it doesn't exist, create private character copy (of public/random character)
      if (!privateCharId) {
        const newCharId = generateBase58Id(TABLE_PREFIXES.characters);
        now = new Date().toISOString();
        // WARNING: Be careful not trying to insert character columns that don't exist due to joins (chats, settings)
        const copied = omit(character, [
          'id',
          'chats', // from join
          'created_at',
          'updated_at',
          'deleted_at',
          'parent_char_id',
          'created_by',
          'community_char_meta',
          'community_approved',
          'flagged',
          'fts',
          'memory_journal',
          'settings_id',
          'settings', // from join
          'use_char_scope_setg',
        ]);
        const { data: newChar, error: insertCharError } = await supabaseEh
          .from('characters')
          .insert([
            {
              ...copied,
              id: newCharId,
              character_scope: 'private',
              use_char_scope_setg: true,
              parent_char_id: character.id,
              created_by: user.id,
              created_at: now,
            }
          ])
          .select('*')
          .single();
        if (insertCharError || !newChar) throw insertCharError || new Error('Failed to create private character');
        privateCharId = newChar.id;
        privateChar = newChar;
      }
      // 3. Create chat row
      if (import.meta.env.DEV) console.log('[DEV] creating chat row');
      const newChatId = generateBase58Id(TABLE_PREFIXES.chats);
      now = new Date().toISOString();
      const { data: userDetails, error: userDetailsError } = await supabaseEh
        .from('user_details')
        .select('id')
        .eq('user_id', user.id)
        .single();
      if (userDetailsError || !userDetails) {
        if (import.meta.env.DEV) {
          trackEvent(USER_EVENTS.USER_ERROR, {
            source: 'ChatPage.loadChatResources',
            error: `Failed to get user_details: ${userDetailsError ? JSON.stringify(userDetailsError) : 'No row found'}`,
            userId: user.id,
          });
        }
      }
      const { data: chat, error: chatError } = await supabaseEh
        .from('chats')
        .insert([
          {
            id: newChatId,
            user_id: user.id,
            ...(userDetails ? { user_details_id: userDetails.id } : {}),
            character_id: privateCharId,
            created_at: now,
          }
        ])
        .select('*')
        .single();
      if (chatError || !chat) throw chatError || new Error('Failed to create chat');
      chatId.current = chat.id;
      
      // Track chat creation
      trackChatCreated(chat.id, privateCharId);
      
      // 4. Send first message if provided after inserting starter message for consistency with LLM
      if (firstMessageToSend) {
        await sendMessageToCharacter(firstMessageToSend, { prependStarter: true });
        // setInput(''); // depends on request status, handled by sendMessageToCharacter
        setPendingMessage(null);
      }
      if (askingVideo) {
        // Insert starter message manually for consistency since we're not calling eh-chat-message
        const starterMessageId = generateBase58Id(TABLE_PREFIXES.messages);
        await supabaseEh
          .from('messages')
          .insert([
            {
              id: starterMessageId,
              chat_id: chatId.current,
              msg_text: starterMessage,
              sender_id: null, // null => AI message
              msg_type: 'text',
            }
          ]);
        // not awaiting video request to avoid blocking the UI
        handleRequestVideo(getProfileImageOrVideoMsg(privateChar || character));
      }
      // Update URL and fetch messages for new chat
      navigate(`/c/${character.url_slug}/${getIdWithoutPrefix(chat.id)}`);
    } catch (err) {
      notWaitingForResponseAnymore();
      
      // Track chat error
      trackChatError({
        chatId: chatId.current || 'unknown',
        characterId: privateChar?.id || character?.id || 'unknown',
        error: `Failed to load chat resources: ${JSON.stringify(err)}`
      });
      
      if (import.meta.env.DEV) {
        console.error('[DEV] Failed to load chat resources:', err);
        setError(err instanceof Error ? err.message : '[DEV] Failed to start chat');
      } else {
        setError('An error occurred while loading chat.');
      }
    } finally {
      setLoading(false);
    }
  }

  // After login, if there is a pending message, send it
  const handleAuthDialogOpenChange = async (open: boolean, { onboardingCompleted, user }: {
    onboardingCompleted?: boolean,
    user?: User
  }) => {
    const u = user || (await supabase.auth.getSession()).data.session?.user;
    if (import.meta.env.DEV) {
      console.log(`[DEV] handleAuthDialogOpenChange open: ${open} character: ${
        character?.url_slug} onboardingCompleted: ${onboardingCompleted} pendingMessage: ${pendingMessage}`);
    }
    setAuthDialogOpen(open);
    if (!open && pendingMessage && u && character && onboardingCompleted) {
      await loadChatResources(pendingMessage);
    }
  };
  const handleOnboardingOpenChange = async (open: boolean, { onboardingCompleted, user }: {
    onboardingCompleted?: boolean,
    user?: User
  }) => {
    const u = user || (await supabase.auth.getSession()).data.session?.user;
    if (import.meta.env.DEV) {
      console.log(`[DEV] handleOnboardingOpenChange open: ${open} character: ${
        character?.url_slug} onboardingCompleted: ${onboardingCompleted} pendingMessage: ${pendingMessage}`);
    }
    if (!open && pendingMessage && u && character && onboardingCompleted) {
      await loadChatResources(pendingMessage);
    }
  };

  // Message skeleton component for loading state
  const MessageSkeleton = ({ isUser = false }: { isUser?: boolean }) => (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`${isUser ? 'bg-gradient-to-r from-rose-800/90 to-rose-700/90' : 'bg-zinc-800'} rounded-lg p-4 max-w-[80%]`}>
        <Skeleton className="h-4 w-48 bg-zinc-700" />
        <Skeleton className="h-4 w-32 bg-zinc-700 mt-2" />
      </div>
    </div>
  );
  
  // Loading bubble for waiting for AI response
  const LoadingBubble = () => (
    <div className="mb-4 flex flex-col items-start">
      <div className="relative max-w-[80%] bg-zinc-800 text-white rounded-lg px-4 py-3">
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 rounded-full bg-gray-400 animate-pulse" style={{ animationDelay: '0ms' }}></div>
          <div className="w-2 h-2 rounded-full bg-gray-400 animate-pulse" style={{ animationDelay: '300ms' }}></div>
          <div className="w-2 h-2 rounded-full bg-gray-400 animate-pulse" style={{ animationDelay: '600ms' }}></div>
        </div>
      </div>
    </div>
  );

  // Fetch all undeleted chats for the user
  useEffect(() => {
    if (!session?.user) return;
    setChatListLoading(true);
    setChatListError(null);
    // Fetch chats, join with character, and get last message
    (async () => {
      const { data: chats, error } = await supabaseEh
        .from('chats')
        .select(`
          id,
          created_at,
          chat_metadata,
          character_id,
          character:character_id (
            first_name, 
            last_name, 
            url_slug, 
            avatar_fpath, profile_media_fpaths
          ),
          messages(id, msg_text, msg_type, created_at)
        `)
        .eq('user_id', session.user.id)
        .is('deleted_at', null)
        .order('created_at', { referencedTable: 'messages', ascending: false })
        .limit(1, { referencedTable: 'messages' })
        .order('updated_at', { ascending: false });
      if (error) {
        setChatListError('Failed to load chats');
        setChatList([]);
        setChatListLoading(false);
        return;
      }

      // For each chat, get the last message (by created_at desc)
      const chatItems: ChatListItem[] = (chats || []).map((chat) => {
        let lastMsg = null;
        if (Array.isArray(chat.messages) && chat.messages.length > 0) {
          lastMsg = chat.messages.reduce((a, b) => (a.created_at > b.created_at ? a : b));
        }
        return {
          id: chat.id,
          created_at: chat.created_at,
          character_id: chat.character_id,
          character: chat.character || {},
          chat_metadata: chat.chat_metadata as ChatListItem['chat_metadata'],
          lastMessage: lastMsg
            ? {
              msg_text: lastMsg.msg_text,
              msg_type: lastMsg.msg_type,
              created_at: lastMsg.created_at
            } : undefined,
        };
      })
      .sort((a, b) => { // chat with most recent message first, or created last if no message
        if (!a.lastMessage && !b.lastMessage) return -a.created_at.localeCompare(b.created_at);
        if (!a.lastMessage) return 1;
        if (!b.lastMessage) return -1;
        return -a.lastMessage.created_at.localeCompare(b.lastMessage.created_at);
      });
      setChatList(chatItems);
      setChatListLoading(false);

      // redirect to last chat if no charSlug ('/c' route)
      if (!charSlug && chatItems.length > 0) {
        chatId.current = chatItems[0].id;
        navigate(`/c/${chatItems[0].character.url_slug}/${chatItems[0].id.replace('chat_', '')}`);
      } else if (!charSlug && chatItems.length === 0) { // or home if no chats
        navigate('/', { replace: true });
      }
    })();
    // omit charSlug and navigate, for which we don't need reactivity
    // add chatIdSlug to fetch after chat creation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatIdSlug, session?.user]);

  // Delete chat and its messages
  const handleDeleteChat = async (chatIdToDelete: string) => {
    const chat = chatList.find(c => c.id === chatIdToDelete);

    if (!chat) {
      if (import.meta.env.DEV) console.error('[DEV] Chat not found:', chatIdToDelete);
      trackChatError({
        chatId: chatIdToDelete,
        characterId: 'unknown',
        error: `Chat not found: ${chatIdToDelete}`
      });
      return;
    }
    const charName = getDisplayName(chat.character);

    if (!window.confirm(`Are you sure you want to delete the conversation with ${charName}? All messages will be lost.`)) return;

    setChatList(prev => prev.filter(c => c.id !== chatIdToDelete));

    // Track chat deletion event
    trackChatDeleted(chatIdToDelete, chat.character_id || 'unknown');

    await supabaseEh
      .from('messages')
      .delete()
      .eq('chat_id', chatIdToDelete);
    await supabaseEh
      .from('chats')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', chatIdToDelete);
    if (chatId.current === chatIdToDelete) {
      const remainingChats = chatList.filter(c => c.id !== chatIdToDelete);
      if (remainingChats.length > 0) {
        chatId.current = remainingChats[0].id;
        navigate(`/c/${remainingChats[0].character.url_slug}/${remainingChats[0].id.replace('chat_', '')}`);
      } else {
        navigate('/', { replace: true });
      }
    }
  };

  const toggleChatList = (open: boolean) => {
    if (!open && messageFormRef.current) { // scroll down when closing dialog
      messageFormRef.current.scrollIntoView({ behavior: 'smooth' });
    } else if (open) {
      window?.scrollTo({ top: 0, behavior: 'instant' }); // fallback, code below doesn't work in some browsers, still debugging it
      if (headerRef?.current) {
        headerRef.current.scrollIntoView({ behavior: 'instant' });
      } else {
        trackEvent(UI_EVENTS.UI_ERROR, {
          source: 'ChatPage.toggleChatList',
          error: 'Header ref not found',
        });
      }
    }
    setShowChatList(open);
  };

  const handleDeleteMessage = useCallback(async (message: Message) => {
    if (!message?.id) return;
    if (!window.confirm('Delete this message? This cannot be undone.')) return;
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) return;
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/eh-chat-message`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionData.session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ messageId: message.id }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        if (import.meta.env.DEV) console.error('[DEV] Failed to delete message', text);
        setError(import.meta.env.DEV ? `[DEV] Failed to delete message: ${text}` : 'Failed to delete message');
        return;
      }
      setMessages(prev => prev.filter(m => m.id !== message.id));
    } catch (e) {
      if (import.meta.env.DEV) console.error('[DEV] Exception deleting message', e);
      setError(import.meta.env.DEV ? `[DEV] ${e instanceof Error ? e.message : String(e)}` : 'Failed to delete message');
    }
  }, []);

  // Handle chat or character selection
  const handleSelectChatOrChar = (idOrSlug: string) => {
    toggleChatList(false);

    if (!idOrSlug.startsWith('chat_')) {
      chatId.current = '';
      // New chat with character
      navigate(`/c/${idOrSlug}`);
      return;
    }

    const id = idOrSlug;
    // Find chat and character
    const chat = chatList.find(c => c.id === id);
    // redirect to default chat page (last chat) if chat.id is not found
    if (!chat) return void navigate(`/c`, { replace: true });
    // We need to update the chatId ref for next fetch
    // since we're already on the ChatPage and URL won't be parsed again
    chatId.current = id;
    navigate(`/c/${chat.character.url_slug}/${id.replace('chat_', '')}`);
  };

  const handleShowSummary = () => {
    if (!isLargeScreen) setShowSummary(true);
  };

  // MARK: Align this logic with back-end (in eh-chat-message in particular)
  const nsfwEnabled = isSubscriber
    ? (userDetails?.nsfw_pref && character?.settings?.nsfw !== false) || character?.settings?.nsfw === true
    : false;

  // Function to handle NSFW toggle
  const handleNsfwToggle = async () => {
    trackEvent(USER_EVENTS.USER_SETTING_NSFW_SWITCH_CLICKED, {
      sourceContext: 'ChatPage',
      sourceId: chatId.current,
      isSubscriber,
      teasing: !isSubscriber && !nsfwEnabled,
      newValue: !nsfwEnabled
    });

    if (!isSubscriber && !nsfwEnabled) {
      setTeaseNsfwEnabled(true);
      teaseNsfwTimeoutRef.current = setTimeout(() => {
        setTeaseNsfwEnabled(false);
      }, 500);
      const user = (await supabase.auth.getSession()).data.session?.user;
      showUpgradeDialogTimeoutRef.current = setTimeout(() => {
        if (user) setShowNsfwUpgradeDialog(true);
        else setAuthDialogOpen(true);
      }, 600);
      return;
    }
    
    // MARK: update the user_details.nsfw_pref, not settings.nsfw that is only used for characters
    try { 
      // TODO: confirmation dialog to update global settings or create/update character settings
      await updateUserDetails({ nsfw_pref: !nsfwEnabled });
    } catch (err) {
      if (import.meta.env.DEV) console.error('[DEV] Failed to update user_details.nsfw_pref:', err);
    }
  };

  /**
   * Checks whether the given message has_nsfw_request in msg_metadata and nsfwEnabled is false (meaning an unlock intent),
   * or any message in conversation has_nsfw_request in msg_metadata (whether nsfw is enabled or not)
   */
  const hadNsfwRequest = (msg?: Message) => {
    if (msg) return !nsfwEnabled && (msg.msg_metadata as MessageMetadata)?.has_nsfw_request;
    return messages.some(msg => (msg.msg_metadata as MessageMetadata)?.has_nsfw_request)
  };

  // SEO metadata based on character properties
  const generateSeoMetadata = () => {
    if (!character) return {
      title: 'EveningHoney.AI Girlfriends & Companions | Chat, roleplay, images',
      description: 'Chat, roleplay and generate images with AI companions. Engage now with realistic AI Girlfriends and Boyfriends.'
    };

    const displayName = getDisplayName(character);
    const genderType = character.gender === 'm' ? 'Boyfriend' : 'Girlfriend';
    
    // Build description with character attributes if available
    const attributes = [];
    if (character.age) attributes.push(`${character.age}`);
    if (character.occupation) attributes.push(character.occupation);
    if (character.personality) attributes.push(character.personality);
    
    const attributeText = attributes.length > 0 ? attributes.join(', ') : '';
    
    return {
      title: `Chat with ${displayName} - AI ${genderType} | EveningHoney.AI`,
      description: `Chat, roleplay, enjoy images & videos with ${displayName}${attributeText ? `, ${attributeText}` : ''}. Engage now with AI ${genderType}s in immersive experience.`,
      ogImage: getProfileImageUrl(character) || undefined
    };
  };

  const { title, description, ogImage } = generateSeoMetadata();

  const handleRequestVideo = async (sourceMessage: Message, { mrId, requestId }: { mrId?: string | null, requestId?: string } = {}) => {
    if (videoStatus === 'pending') return;
    setVideoStatus('pending');

    const { data: sessionData } = await supabase.auth.getSession();
    if (!session?.user || !sessionData?.session) {
      setVideoStatus('idle');
      return;
    }

    // 1. Check if user is eligible (first one free, or subscriber with enough gems, or monthly free video)
    const oneMonthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    const { data: videoRequests, error } = await supabaseEh
      .from('media_requests')
      .select('id, created_at')
      .eq('user_id', session.user.id)
      .eq('media_type', 'chat_video')
      .eq('status', 'success')
      .order('created_at', { ascending: false })
      .limit(FREE_VIDEOS + 1);

    if (error) {
      if (import.meta.env.DEV) console.error('[DEV] Error checking for previous videos:', error);
      // proceed and let backend handle it
    }

    const monthlyFreeVideo = !isSubscriber && videoRequests?.[0]?.created_at && videoRequests[0].created_at < oneMonthAgo;
    const hasFreeVideo = (videoRequests?.length || 0) < FREE_VIDEOS || monthlyFreeVideo;

    if (!hasFreeVideo) {
      if (!isSubscriber && balance.total < COST_PER_VIDEO) {
        setLowGemBalanceAction('video');
        setShowGemsDialog(true);
        // setLimitData({ limitType: 'video' });
        // setShowUpgradeDialog(true);
        setVideoStatus('idle');
        return;
      }
      // Handle 402 error generated server-side as for images, using fetch to call eh-media-request below
      // if (balance.total < COST_PER_VIDEO) {
      //   setLowGemBalanceAction('video');
      //   setShowGemsDialog(true);
      //   return;
      // }
    }

    if (!chatIdSlug) { // create chat before requesting video
      // TODO: create chat with character if not exists
      return loadChatResources(undefined, { askingVideo: true });
    }

    setPendingVideoOriginMsgId(sourceMessage.id);

    // Auto-scroll if it is not a retry
    if (!mrId && !requestId) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
    
    // 2. Call eh-media-request
    const id = mrId || generateBase58Id(TABLE_PREFIXES.media_requests);
    // Set pending video state in chat metadata for UI
    const chatItem = chatList.find(c => c.id === chatId.current);
    const { data: chat, error: chatError } = await supabaseEh
      .from('chats')
      .update({ chat_metadata: {
        ...(chatItem?.chat_metadata ?? {}),
        pending_video_origin_msg_id: sourceMessage.id,
        pending_video_at: new Date().toISOString(),
        pending_video_mr_id: id,
      } })
      .eq('id', chatId.current)
      .select('id, chat_metadata')
      .maybeSingle();
    if (chatError) {
      trackChatError({
        chatId: chatId.current || 'unknown',
        characterId: character?.id || 'unknown',
        error: `Error updating chat metadata for video request: ${chatError.message}`
      });
    }
    const removePendingVideoAt = async (chat: { id: string, chat_metadata?: Json } | null) => {
      if (!chat) return;
      await supabaseEh.from('chats')
        .update({ chat_metadata: {
          ...(chat?.chat_metadata as ChatListItem['chat_metadata'] ?? {}),
          pending_video_origin_msg_id: null,
          pending_video_mr_id: null,
          pending_video_at: null
        } })
        .eq('id', chatId.current);
      setPendingVideoOriginMsgId('');
    };
    // Same media request logic as in StudioPage.tsx
    try {
      const sourceMessageMediaData = sourceMessage.msg_media_data as MessageMediaData;
      // Detect Edge function timeout limit reached to open a new connection
      videoAbortControllerRef.current = new AbortController();
      videoAbortTimeoutRef.current = setTimeout(() => {
        videoAbortControllerRef.current?.abort('Edge function timeout');
      }, 1000 * 140); // Edge function Gateway timeout is 150s

      let resource_id = (sourceMessage.id !== 'profile-img' ? sourceMessage.id  : character?.id) || charSlug;
      if (sourceMessage.id !== 'profile-img') {
        resource_id = sourceMessageMediaData?.mr_id ?? resource_id;
      }

      const videoImgDescription = sourceMessage.id === 'profile-img'
        ? (character?.char_metadata as { imagePrompt?: string })?.imagePrompt
          || (character?.char_metadata as { from_prompt?: string })?.from_prompt // character generated from image
          || (character?.char_metadata as { profile_img_desc?: string })?.profile_img_desc // official character
        : '';

      if (!resource_id) {
        trackChatError({
          chatId: sourceMessage.chat_id || charSlug || 'unknown',
          characterId: character?.id || 'unknown',
          error: `No resource_id for video media-request ${id}`,
        });
      }

      const mediaRequestResponse = await fetch(`${SUPABASE_URL}/functions/v1/eh-media-request`, {
        signal: videoAbortControllerRef.current.signal,
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionData.session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mrId: id,
          media_type: 'chat_video',
          characterId: character?.id,
          chatId: sourceMessage.chat_id,
          videoImgMessageId: sourceMessage.id,
          resource_id,
          safeMode: !is18Confirmed,
          videoSourceImageUrl: sourceMessage.msg_media_fpath,
          videoImgDescription: sourceMessageMediaData?.img_description || videoImgDescription,
          videoImgPrompt: sourceMessageMediaData?.img_prompt,
          style: character?.style,
          requestId
        })
      });

      if (!mediaRequestResponse.ok && mediaRequestResponse.status === 402) {
        setVideoStatus('failed');
        // setError(`Please ${isSubscriber ? 'buy more gems' : 'subscribe'} to generate more videos.`);
        await refreshBalance();
        setShowGemsDialog(true);
        await removePendingVideoAt(chat);
        return { success: false };
      } else if (!mediaRequestResponse.ok && videoRetryCountRef.current > 0) { // retry only once after error
        setVideoStatus('failed');
        if (import.meta.env.DEV) console.error('[DEV] Error requesting video:', await mediaRequestResponse.text());
        setError('Oops, something went wrong. Please try again.');
        await removePendingVideoAt(chat);
        return { success: false };
      }

      const mediaData = await mediaRequestResponse.json();
      
      if (!mediaRequestResponse.ok || (mediaData?.retryAfter && videoRetryCountRef.current < 10)) {
        videoRetryCountRef.current++;
        // retryAfter is an epoch timestamp, compute delay in ms (min 10s)
        const delay = Math.max(mediaData?.retryAfter ? mediaData.retryAfter - Date.now() : 10000, 10000);
        videoRetryTimeoutRef.current = setTimeout(async () => {
          await handleRequestVideo(sourceMessage, {
            requestId: mediaData?.requestId || undefined,
            // mrId can be provided to convey that requestId is not available yet, but media_request row is already created
            mrId: mediaData?.mrId || undefined,
          });
        }, Math.max(delay, 0));
        return { success: false, retrying: true };
      }

      if (import.meta.env.DEV) console.log('[DEV] handleMediaRequest data:', mediaData);

      if (!mediaData?.success || !mediaData?.filePath) {
        setVideoStatus('failed');
        await removePendingVideoAt(chat);
        return { success: false };
      }

      await fetchMessages({ nbPreviousMessages: messages.length });
      setVideoStatus('success');
      await refreshBalance();
      await removePendingVideoAt(chat);
      if (!isSubscriber) {
        setLimitData({ limitType: 'video' });
      }

      videoRetryCountRef.current = 0; // Reset retry count on success
      return { success: true, data: mediaData };
    } catch (err) {
      if (import.meta.env.DEV) console.log('[DEV]', err)
      if ((err instanceof DOMException && err.name === 'AbortError')
        || typeof err === 'string' && err.toLowerCase().includes('timeout'))
      { // Edge function Gateway timeout
        // call again without incrementing retryCount since it's not a retry after real failure
        return handleRequestVideo(sourceMessage, { mrId: id });  // use mrId since we got no requestId to reference
      }

      // Do not show error state and remove pending video from chat if media request is simply aborted on unmount
      if (!(typeof err === 'string' && err.toLowerCase().includes('unmount'))) {
        setVideoStatus('failed');
        setError(import.meta.env.DEV && err instanceof Error ? `[DEV] ${err.message}` : 'Oops, something went wrong. Please try again.');
        await removePendingVideoAt(chat);
      }
      return { success: false };
    }
  };

  const handleUpgradeClick = () => {
    if (!session?.user) {
      setAuthDialogOpen(true);
      return false;
    }
    return true;
  };

  // Don't automatically show the mode switch in mobile apps after sfw image, unless user is subscriber
  const canShowNsfwPrompt = (!isFromAppStores() || hadNsfwRequest() || isSubscriber)
    // should direct iOS users to enable the option on the website
    // && (!isFromAppleAppstore());

  /**
   * Detect languages in chat messages and determine TTS availability
   * @returns Object with TTS availability info
   */
  const detectChatLanguages = useCallback(() => {
    if (!messages.length) return { canShowTTS: true, hasUnsupportedLanguage: false, languages: [] };
    
    const languages = messages
      .map(msg => (msg.msg_metadata as MessageMetadata)?.language)
      .filter(Boolean) as string[];
    
    const uniqueLanguages = [...new Set(languages)];
    const hasUnsupportedLanguage = uniqueLanguages.some(lang => 
      lang && !SUPPORTED_TTS_LANGUAGES.includes(lang)
    );
    
    // If any unsupported language, hide all TTS buttons
    if (hasUnsupportedLanguage) {
      return { canShowTTS: false, hasUnsupportedLanguage: true, languages: uniqueLanguages };
    }
    
    // If all messages are English or no language set, show TTS normally
    const hasOnlyEnglishOrNone = uniqueLanguages.length === 0 || 
      (uniqueLanguages.length === 1 && uniqueLanguages[0] === 'en');
    
    if (hasOnlyEnglishOrNone) {
      return { canShowTTS: true, hasUnsupportedLanguage: false, languages: uniqueLanguages };
    }
    
    // If all languages are supported, show TTS but may need voice selection
    return { canShowTTS: true, hasUnsupportedLanguage: false, languages: uniqueLanguages };
  }, [messages]);

  /**
   * Check if a message's language is supported by the current character voice
   * @param message - The message to check
   * @returns true if language is supported by current voice
   */
  const isMessageLanguageSupportedByVoice = useCallback((message: Message): boolean | null => {
    // Default voices support English
    const voiceOption = VOICE_OPTIONS.find(voice => voice.value === character?.voice) || { languages: ['en'] };

    const messageLanguage = (message.msg_metadata as MessageMetadata)?.language;
    // No language specified: assume supported if (default) voice supports English
    if (!messageLanguage && voiceOption.languages.includes('en')) return true; // assume message is in English
    else if (!messageLanguage) return null; // we don't know for sure, non-English language may be missing in LLM response

    if (!character?.voice && messageLanguage && messageLanguage !== 'en') return false;
    else if (!character?.voice) return true;
    
    return voiceOption?.languages?.includes(messageLanguage) || false;
  }, [character?.voice]);

  /**
   * Handle TTS button click with language detection
   * @param message - The message to generate TTS for
   * @param onLoginRequired - Callback for when login is required
   */
  const handleTTSButtonClick = useCallback((message: Message) => {
    // Check if we've already shown voice selection dialog for this chat
    const voiceSelectionShown = JSON.parse(
      storage.getItem(LS_TTS_VOICE_SELECTION_SHOWN_KEY) || '{}'
    );
    const hasShownForChat = voiceSelectionShown[chatId.current] && voiceSelectionShown[chatId.current] > Date.now() - 1000 * 60 * 60 * 24;

    const isSupported = isMessageLanguageSupportedByVoice(message);
    // If message language is supported by current voice, proceed normally
    if (isSupported === true) return true;
    else if (isSupported === null) {
      // If we're not sure and we've already shown the dialog for this chat recently, proceed anyway
      if (hasShownForChat) return true;
    }

    // Get message language and find best voice for it
    const messageLanguage = (message.msg_metadata as MessageMetadata)?.language || 'en';
    if (messageLanguage && SUPPORTED_TTS_LANGUAGES.includes(messageLanguage)) {
      const bestVoice = findBestVoiceForLanguage(messageLanguage, character?.gender as 'f' | 'm');
      
      if (bestVoice) {
        // Set context and select voice for the dialog
        const languageLabels: Record<string, string> = {
          en: 'English', fr: 'French', zh: 'Chinese', hi: 'Hindi', ja: 'Japanese', pt: 'Portuguese', es: 'Spanish'
        };
        
        setTtsLanguageContext(
          `This message is in ${languageLabels[messageLanguage] || messageLanguage}. ` +
          `Please select a voice that supports ${languageLabels[messageLanguage] || messageLanguage} for better pronunciation. ` +
          `You can always change the voice in the character profile.`
        );
        setTtsSelectedVoice(bestVoice);
        setIsEditCharacterDialogOpen(true);

        if (chatId.current) {
          const voiceSelectionShown = JSON.parse(
            storage.getItem(LS_TTS_VOICE_SELECTION_SHOWN_KEY) || '{}'
          );
          voiceSelectionShown[chatId.current] = Date.now();
          storage.setItem(LS_TTS_VOICE_SELECTION_SHOWN_KEY, JSON.stringify(voiceSelectionShown));
        }
        return false;
      }
    }

    // Fallback: proceed with TTS (will use current voice) but should not happen since language is not supported
    if (import.meta.env.DEV) console.log('[DEV] TTS for potentially unsupported language:', messageLanguage);
    return true;
  }, [isMessageLanguageSupportedByVoice, character?.gender]);

  const canShowTTS = useMemo(() => detectChatLanguages().canShowTTS, [detectChatLanguages]);

  return (
    <div className={`flex-auto flex flex-col max-h-dvh sm:max-h-[calc(100dvh-57px)] w-full bg-zinc-900`}>
      <SEO
        title={title}
        description={description}
        ogImage={ogImage}
        ogUrl={window.location.pathname}
        ogType="profile"
      />
      <EhEndorselyScript />
      
      <div className="flex flex-col sm:flex-row flex-1 w-full 2xl:container mx-auto shadow-lg border-x border-zinc-800 overflow-hidden">
        {/* Chat list sidebar (desktop) */}
        <div className="hidden lg:block max-w-[320px] min-w-[220px] border-r border-zinc-800 overflow-y-auto">
          <ChatListView
            chats={chatList}
            loading={chatListLoading}
            error={chatListError}
            activeChatId={chatId.current}
            onSelect={handleSelectChatOrChar}
            onDelete={handleDeleteChat}
          />
        </div>
        {/* Chat list overlay (mobile/tablet) */}
        {showChatList && (
          <div className={`fixed inset-x-0 bottom-0 z-40 bg-black/80 lg:hidden`} style={{ top: EH_HEADER_HEIGHT }}>
            <ChatListView
              chats={chatList}
              loading={chatListLoading}
              error={chatListError}
              activeChatId={chatId.current}
              onSelect={handleSelectChatOrChar}
              onDelete={handleDeleteChat}
              onClose={() => toggleChatList(false)}
            />
          </div>
        )}
        {/* Main chat area */}
        <div className="flex flex-col min-w-1/2 flex-1 w-full overflow-y-auto" style={{ scrollBehavior: 'smooth' }}>
          {/* Character header */}
          <div className="flex items-center gap-2 sm:gap-4 px-2 lg:px-4 py-1.5 lg:py-3 border-b border-zinc-800 bg-zinc-900 sticky top-0 z-10">
            {/* Chevron for mobile/tablet */}
            <button
              className="lg:hidden p-2 pr-1 rounded-full hover:bg-zinc-700"
              onClick={() => toggleChatList(true)}
              aria-label="Show chat list"
            >
              <ChevronLeft className="size-6 text-white" />
            </button>
            {(loading && !loadingCharacter) ? (
              <div className="flex items-center gap-4">
                <Skeleton className="rounded-full h-12 w-12 lg:h-[78px] lg:w-[78px] bg-zinc-700" />
                <Skeleton className="h-6 w-32 bg-zinc-700" />
              </div>
            ) : (character || customImage) ? (
              <>
                <button
                  className="lg:hidden"
                  onClick={handleShowSummary}
                  aria-label="Show character summary"
                >
                  {getProfileImageUrl(character) && (
                    <img
                      src={getProfileImageUrl(character)}
                      alt={character?.first_name || 'Avatar'}
                      className="rounded-full"
                      style={{ width: 64, height: 64, objectFit: 'cover' }}
                    />
                  )}
                </button>
                <div
                  className="hidden lg:block"
                >
                  {getProfileImageUrl(character) && (
                    <img
                      src={getProfileImageUrl(character)}
                      alt={character?.first_name || 'Avatar'}
                      className="rounded-full"
                      style={{ width: AVATAR_SIZE, height: AVATAR_SIZE, objectFit: 'cover' }}
                    />
                  )}
                </div>
                <div className="select-none max-w-1/2">
                  {character ? (
                    <>
                      <h2 className="text-lg font-bold text-white" onClick={handleShowSummary}>
                        {character?.first_name} {character?.last_name}
                      </h2>
                      <div className="flex items-center text-xs -mt-0.5 text-zinc-400/50">
                        <span className="max-w-3/4 truncate capitalize">{character.relationship}</span>
                        <IdCard className="lg:hidden size-4 ml-1" onClick={handleShowSummary} />
                      </div>
                      {(is18Confirmed || nsfwEnabled) // let user opt out if enabled
                        // show if there is any image in conversion or nsfw content request in any message
                        && (displayMessages.filter(m => m.msg_type === 'image' && m.id !== 'profile-img').length > 0 || hadNsfwRequest())
                        && canShowNsfwPrompt
                        && (
                        <div className="flex items-center gap-2 mt-1">
                          <div 
                            className="text-sm bg-gradient-to-r from-purple-500 to-pink-600 bg-clip-text text-transparent font-semibold"
                          >
                            {isFromAppStores() ? 'Unrestricted' : 'NSFW'}
                          </div>
                          <Switch 
                            checked={nsfwEnabled || teaseNsfwEnabled}
                            onCheckedChange={handleNsfwToggle}
                            aria-label={`Toggle ${isFromAppStores() ? 'Unrestricted' : 'NSFW'} content`}
                            className="data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-purple-600 data-[state=checked]:to-pink-600"
                          />
                        </div>
                      )}
                    </>
                  ) : loadingCharacter ? (
                    <h2 className="text-lg font-bold text-white">Connecting...</h2>
                  ) : null}
                </div>
              </>
            ) : null}
            <Button variant="pink" className="rounded-full ml-auto mr-2 sm:mr-4 size-10 sm:size-14 p-2 sm:p-3" onClick={async () => {
              if (!session?.user) {
                setAuthDialogOpen(true);
                return;
              }
              if (!chatId.current) await loadChatResources();
              if (!character || !chatId.current) return; // failsafe
              if (!isSubscriber) {
                setLimitData({ limitType: 'call' });
                setShowUpgradeDialog(true);
                return;
              }
              preCall(character, chatId.current);
            }}>
              <Phone className="size-6 sm:size-8" />
              <span className="sr-only">Phone Call</span>
            </Button>
          </div>

          {/* Message list */}
          <div
            ref={messagesContainerRef}
            className="flex-1 overflow-y-auto px-2 sm:px-6 pt-4 sm:pt-8 bg-zinc-900"
            style={{ scrollBehavior: 'smooth' }}
          >
            {(loading && !error) ? (
              <div className="space-y-4">
                <MessageSkeleton />
                <MessageSkeleton isUser={true} />
                <MessageSkeleton />
                {loadingCharacter && (
                  <div className="flex h-full flex-col items-center justify-center gap-4 mt-8">
                    <div className="size-16 border-4 border-zinc-600 border-t-pink-500 rounded-full animate-spin"></div>
                    <p className="text-white text-lg font-medium">Getting in touch</p>
                    <p className="text-zinc-400 text-center max-w-md">
                      This may take a moment. Why not think about what you want to say? 💬
                    </p>
                  </div>
                )}
              </div>
            ) : error ? (
              <div className="flex h-full items-center justify-center">
                <div className="bg-red-900/20 border border-red-800 text-white rounded-lg p-6 max-w-md">
                  <p className="text-center">{error}</p>
                  <button
                    className="mx-auto bg-pink-700 rounded-lg text-white font-bold mt-4 p-3 flex items-center justify-center"
                    onClick={() => window?.location.reload()}
                  >
                    <RefreshCcw className="mr-2" />
                    <span>Reload chat</span>
                  </button>
                  <button
                    className="mx-auto bg-pink-700 rounded-lg text-white font-bold mt-4 p-3 flex items-center justify-center"
                    onClick={() => navigate('/')}
                  >
                    See other characters
                  </button>
                </div>
              </div>
            ) /*: loadingCharacter ? (
              <div className="flex h-full flex-col items-center justify-center space-y-4">
                <div className="size-16 border-4 border-zinc-600 border-t-pink-500 rounded-full animate-spin"></div>
                <p className="text-white text-lg font-medium">Creating your character...</p>
                <p className="text-zinc-400 text-center max-w-md">
                  We're generating a unique character based on the provided image. This may take a moment.
                </p>
              </div>
            )*/ : (
              <>
                {displayMessages.map((msg, idx, arr) => {
                  const isUserMessage = msg.sender_id === session?.user?.id;
                  const isPendingVideoPlaceholder = msg.id === 'pending-video'
                  const isBlurred = ((msg.msg_media_data as MessageMediaData)?.blur && !isSubscriber) || isPendingVideoPlaceholder;
                  const isChargedImage = msg.msg_type === 'image' && (msg.msg_media_data as MessageMediaData)?.is_charged;
                  // isChargedImage was not set on older images
                  const isRequestedImageMessageCharged = isChargedImage !== false && msg.msg_type === 'image' && idx > 0
                    && (arr[idx - 1].msg_metadata as MessageMetadata)?.has_user_image_request;

                  const faceErrorMessage = 'Sorry, I have trouble generating an image based on my profile picture';

                  return (
                    <div 
                      key={msg.id} 
                      className={`mb-4 flex flex-col ${isUserMessage ? 'items-end' : 'items-start'}`}
                    >
                      <div
                        className={`relative rounded-lg ${
                          isUserMessage 
                            ? 'bg-gradient-to-r from-rose-800/90 to-rose-700/90 text-white' 
                            : 'bg-zinc-800 text-white'
                          } ${
                            ['image', 'video'].includes(msg.msg_type || '') ? 'max-w-[calc(min(50%,416px))]' : 'max-w-2/3 p-3'
                          } ${
                            (isBlurred && !isPendingVideoPlaceholder) ? 'cursor-pointer' : ''
                          }`
                        }
                        tabIndex={isBlurred ? 0 : -1}
                        aria-label={isBlurred ? 'Upgrade to reveal image' : undefined}
                        onClick={() => {
                          if (isBlurred && !isPendingVideoPlaceholder) {
                            setLimitData({ limitType: hadNsfwRequest(displayMessages[idx - 1]) ? 'nsfw' : 'image' });
                            setShowUpgradeDialog(true);
                          }
                        }}
                      >
                        {(msg.msg_type === 'video' && msg.msg_media_fpath) ? (
                          <EhVideoMessage
                            autoPlay={msg.id !== 'profile-video'}
                            mediaPathOrUrl={msg.msg_media_fpath}
                            posterUrl={getMessageImage((msg.msg_media_data as MessageMediaData)?.video_img_message_id, { fullUrl: true })}
                          />
                        ) : (msg.msg_type === 'image' && msg.msg_media_fpath) ? (
                          <>
                            <EhImageMessage
                              blur={isBlurred}
                              pendingMediaPlaceholder={isPendingVideoPlaceholder ? 'Generating video...' : ''}
                              charFullName={getDisplayName(character || {})}
                              mediaPathOrUrl={msg.msg_media_fpath}
                              description={(msg.msg_media_data as MessageMediaData)?.img_description || (msg.msg_media_data as MessageMediaData)?.img_prompt}
                              userToken={session?.access_token || ''}
                              isCharged={isRequestedImageMessageCharged}
                              isSubscriber={isSubscriber}
                            />
                            {/* Assume the previous message is the origin message for this image. Don't show the nsfw warning if the image had nsfw enabled */}
                            {idx > 0
                              && hadNsfwRequest(displayMessages[idx - 1])
                              && !((msg.msg_media_data as MessageMediaData)?.nsfw)
                              && canShowNsfwPrompt
                              && (
                              <div className="text-xs p-2 italic text-zinc-300">
                                <span>Want to see more right now? Enable</span>
                                <strong className="bg-gradient-to-r from-purple-500 to-pink-600 bg-clip-text text-transparent">
                                  {` ${isFromAppStores() ? 'Unrestricted' : 'NSFW'} `}
                                </strong>
                                <span>☝️</span>
                                <br />
                                {!isBlurred && (
                                  <>
                                    <span>You can also try to persuade </span>
                                    <span>{character?.first_name}</span>
                                    <span>{`${isFromAppStores() ? '' : ' to reveal more'}...`} </span>
                                  </>
                                )}
                              </div>
                            )}
                            {((msg.msg_media_data as MessageMediaData)?.skip_input_img_after_face_error) && (
                              <div className="text-xs p-2 italic text-zinc-300">
                                <span>{faceErrorMessage}</span>
                              </div>
                            )}
                          </>
                        ) : msg.msg_type === 'call_summary' ? (
                          <span className="flex items-center gap-2 select-none">
                            <PhoneOutgoing className="size-5" />
                            <span className="text-sm text-foreground">Phone call</span>
                            {msg.msg_media_data && 'call_duration' in (msg.msg_media_data as MessageMediaData) && (
                              <span className="text-sm text-muted-foreground">{
                                `${Math.round((msg.msg_media_data as MessageMediaData).call_duration as number / 60)}min`
                              }</span>
                            )}
                          </span>
                        ) : (
                          <span>{formatMessageText({ text: msg.msg_text || '' })}</span>
                        )}
                        
                        {/* Show loading indicator if image was requested but not yet received */}
                        {getImageRequestStatus(displayMessages, msg) === 'pending' && (
                          <div className={
                            'flex items-center gap-2 mt-3 py-2'
                              + `${msg.msg_text ? ' border-t border-zinc-700' : ''}`
                          }>
                            <span className="w-5 h-5 border-2 border-zinc-600 border-t-pink-500 rounded-full animate-spin"></span>
                            <span className="text-xs text-zinc-400">Generating image...</span>
                          </div>
                        )}
                        {['face_error', 'failed', 'expired', 'low_balance'].includes(getImageRequestStatus(displayMessages, msg))
                        // show only if there's a message or it's the last message
                        && (msg.msg_text || idx === displayMessages.length - 1)
                        && (
                          <div className={
                            'text-xs mt-1 italic text-zinc-300 mt-3 pt-2'
                              + `${msg.msg_text ? ' border-t border-zinc-700' : ''}`
                          }>
                            {getImageRequestStatus(displayMessages, msg) === 'face_error' ? (
                              <span>{faceErrorMessage}</span>
                            ) : (
                              <span>Changed my mind about the image</span>
                            )}
                          </div>
                        )}
                      </div>
                      {msg.msg_type === 'image' && !isPendingVideoPlaceholder && /*(msg.msg_metadata as MessageMetadata)?.has_video_request &&*/ (
                        <Button
                          variant="outline"
                          className={`border-zinc-700 bg-transparent hover:bg-pink-600/20 text-white hover:text-white mt-2${
                            isUnlimited && hasGeneratedVideoForThisImage(displayMessages, msg) ? ' hidden' : ''
                          }`}
                          onClick={() => handleRequestVideo(msg)}
                          disabled={videoStatus === 'pending' || !pendingVideoDetectionDone}
                        >
                          <Video className="size-5" />
                          {videoStatus === 'pending' && pendingVideoOriginMsgId === msg.id ? (
                            <span className="w-5 h-5 border-2 border-zinc-600 border-t-pink-500 rounded-full animate-spin"></span>
                          ) : (
                            <span className="space-x-2">
                              <span>Ask for video</span>
                              {isSubscriber && !isUnlimited ? (
                                <span className="text-xs bg-white/20 rounded-sm p-1">{`${COST_PER_VIDEO} 💎`}</span>
                              ) : limitData.limitType !== 'video' && messages.filter(m => m.msg_type === 'video').length < FREE_VIDEOS && !isUnlimited && (
                                <span className="text-xs bg-white/20 rounded-sm px-2 py-1">Free Trial</span>
                              )}
                            </span>
                          )}
                        </Button>
                      )}
                      <div className={`text-xs mt-2 px-1 text-zinc-400 ${isUserMessage ? 'text-right' : 'text-left'}${
                        msg.id === 'profile-img' ? ' hidden' : ''
                      }`}>  
                        <div className={`flex ${isUserMessage ? 'justify-end' : ''} items-center gap-2`}>
                          {!isUserMessage && canShowTTS && (
                            <TTSButton
                              className={`${isCallActive || msg.msg_type === 'call_summary' ? ' hidden' : ''}`}
                              message={msg}
                              character={character}
                              isSubscriber={isSubscriber}
                              onLoginRequired={() => setAuthDialogOpen(true)}
                              onTTSUsage={(params) => {
                                // TODO: handle rate limit for subscribers
                                if (params?.limit === 'low_gem_balance' && isSubscriber) {
                                  setLowGemBalanceAction('tts');
                                  setShowGemsDialog(true);
                                } else if (!isSubscriber) {
                                  setLimitData({ limitType: 'audio' });
                                  setShowUpgradeDialog(true);
                                }
                              }}
                              onTTSButtonClick={handleTTSButtonClick}
                            />
                          )}
                          {!isUserMessage && ['text', 'image', 'video'].includes(msg.msg_type ?? '') && (
                            <button
                              className="flex items-center justify-center size-8 rounded-full bg-zinc-800 hover:bg-zinc-700"
                              onClick={() => {
                                setFlaggedMessage(msg);
                                setShowFlagDialog(true);
                              }}
                            >
                              <Flag className="size-4 text-zinc-400" />
                            </button>
                          )}
                          {['text', 'image', 'video'].includes(msg.msg_type ?? '') && (
                            <button
                              className="flex items-center justify-center size-8 rounded-full bg-zinc-800 hover:bg-zinc-700"
                              onClick={() => handleDeleteMessage(msg)}
                              aria-label="Delete message"
                            >
                              <Trash2 className="size-4 text-zinc-400" />
                            </button>
                          )}
                          <span>
                            {formatMessageTime(msg.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {isWaitingForResponse && getImageRequestStatus(displayMessages) === 'none'
                // Do not show writing bubble if an image is being generated with animation above
                  && <LoadingBubble />
                }
                <div ref={messagesEndRef} />
                <div ref={scrollToBottomOnLoad} />
              </>
            )}
          </div>

          {/* Message input */}
          <form
            ref={messageFormRef}
            className="flex items-center gap-2 px-4 py-3 border-t border-zinc-800 bg-zinc-900"
            style={{ position: 'sticky', bottom: 0, zIndex: 10 }}
            onSubmit={e => { e.preventDefault(); handleSend(); }}
          >
            <Textarea
              className="flex-1 rounded-lg max-h-32 px-4 py-2 bg-zinc-700 text-white placeholder:text-zinc-400 focus:outline-none"
              placeholder={session?.user ? 'Message' : 'Enter your message'}
              value={input}
              onChange={e => {
                const newValue = e.target.value;
                setInput(newValue);
                // Save draft when input changes
                if (chatId.current || charSlug) debouncedSaveDraft((chatId.current || charSlug) as string, newValue);
              }}
              onFocus={() => {
                if (messageFormRef.current) {
                  messageFormRef.current.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              rows={2}
              maxLength={MAX_MESSAGE_LENGTH}
              disabled={(loading && !loadingCharacter) || !!error}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <button
              type="submit"
              className="bg-pink-700 text-white font-bold p-3 rounded-full disabled:opacity-50 flex items-center justify-center"
              disabled={!input.trim() || loading || loadingCharacter || !!error || isSending || isWaitingForResponse}
            >
              {isSending
                ? <div className="w-5 h-5 border-2 border-white border-t-pink-500 rounded-full animate-spin"></div>
                : <Send className="h-5 w-5" />
              }
            </button>
          </form>
        </div>
        
        {/* Desktop summary panel */}
        {(character || loading || (customImage && loadingCharacter)) && (
          <div className="hidden lg:block max-w-[313px] min-w-[157px] border-l border-zinc-800 overflow-y-auto">
            <CharacterSummaryContent 
              character={character} 
              isLoading={loading} 
              customImage={customImage}
              onEditVoice={chatIdSlug ? () => setIsEditCharacterDialogOpen(true) : undefined}
              onStartCall={handleStartCall}
            />
          </div>
        )}
      </div>
      
      {/* Mobile summary dialog */}
      {character && (
        <CharacterSummaryDialog 
          open={showSummary} 
          onOpenChange={setShowSummary} 
          character={character} 
          isLoading={loading || loadingCharacter}
          customImage={customImage}
          onEditVoice={chatIdSlug ? () => setIsEditCharacterDialogOpen(true) : undefined}
          onStartCall={handleStartCall}
        />
      )}
      <EhAuthDialog
        ref={authDialogRef}
        open={authDialogOpen}
        onOpenChange={handleAuthDialogOpenChange}
        onOnboardingOpenChange={handleOnboardingOpenChange}
        enableOnboardingAutoCheck={true}
        mediaUrl={customImage || getMediaUrl(character?.profile_media_fpaths?.[0] || '')}
        activeFilters={{
          gender: character?.gender as 'f' | 'm' | undefined,
          style: character?.style as 'realistic' | 'anime' | undefined,
        }}
      />
      
      {/* Add NSFW Upgrade Dialog */}
      <EhUpgradeDialog
        open={showNsfwUpgradeDialog}
        onOpenChange={setShowNsfwUpgradeDialog}
        limitType="nsfw"
        characterData={{
          id: character?.id,
          name: getDisplayName(character || {}),
          imageUrl: getProfileImageUrl(character)
        }}
        onUpgradeClick={handleUpgradeClick}
      />
      
      {/* Add Upgrade Dialog for message/image limits */}
      <EhUpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        limitType={limitData.limitType}
        characterData={{
          id: character?.id,
          name: getDisplayName(character || {}),
          imageUrl: getProfileImageUrl(character)
        }}
        onUpgradeClick={handleUpgradeClick}
        // usageStats={limitData.usageStats}
      />
      
      {/* Add Gems Dialog for low gem balance */}
      <EhGemsDialog
        open={showGemsDialog}
        onOpenChange={setShowGemsDialog}
        action={lowGemBalanceAction}
        characterData={{
          id: character?.id,
          name: getDisplayName(character || {}),
          imageUrl: getProfileImageUrl(character)
        }}
      />
      <EditCharacterDialog
        isOpen={isEditCharacterDialogOpen}
        onClose={() => {
          setIsEditCharacterDialogOpen(false);
          setTtsLanguageContext('');
        }}
        character={character}
        onSuccess={handleEditCharacterSuccess}
        onUpgrade={() => {
          setLimitData({ limitType: 'create' });
          setShowUpgradeDialog(true);
        }}
        context={ttsLanguageContext}
        initialSelectedVoice={ttsSelectedVoice}
      />
      {flaggedMessage && (
        <EhFlagDialog
          open={showFlagDialog}
          onOpenChange={setShowFlagDialog}
          flaggedResourceId={flaggedMessage.id}
          onClose={(flaggingSuccess) => {
            setFlaggedMessage(null);
            if (flaggingSuccess) fetchMessages();
          }}
          textPreview={
            flaggedMessage.msg_type === 'text'
              ? flaggedMessage.msg_text ?? ''
              : undefined
          }
          thumbnailUrl={
            flaggedMessage.msg_type === 'image'
              ? getMediaUrl(flaggedMessage.msg_media_fpath || '')
              : undefined
          }
          videoUrl={
            flaggedMessage.msg_type === 'video'
              ? getMediaUrl(flaggedMessage.msg_media_fpath || '')
              : undefined
          }
          flagData={{
            charSlug: character?.url_slug,
            chatId: chatId.current,
            chatMessageType: flaggedMessage.msg_type,
          }}
        />
      )}
    </div>
  );
};
